import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import Nav from "@/components/Nav";

/*
 * Public signup is CLIENT-ONLY.
 * Non-client roles (designer / vendor / agency admin) are invite-only.
 * See /admin/team for admin-side invite creation.
 */
export default function Signup() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get("intent"); // "ai" | "human" | "hire-designer" | null
  const [f, setF] = useState({ email: "", password: "", name: "", company: "" });
  const [loading, setLoading] = useState(false);

  // Already logged in? Skip signup — route to the appropriate destination.
  useEffect(() => {
    if (!user) return;
    const source = intent === "human" || intent === "hire-designer" ? "human"
      : intent === "template" ? "template"
      : intent === "ai" ? "ai" : null;
    const dest = user.role !== "client"
      ? (user.role === "admin" ? "/admin" : user.role === "designer" ? "/designer" : user.role === "vendor" ? "/vendor" : "/dashboard")
      : source ? `/brief/new?source=${source}` : "/dashboard";
    nav(dest, { replace: true });
  }, [user, intent, nav]);

  if (user) return null;

  const update = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({
        email: f.email,
        password: f.password,
        name: f.name,
        company: f.company,
        role: "client",
      });
      toast.success(`Welcome, ${u.name}`);
      const source = intent === "human" || intent === "hire-designer" ? "human"
        : intent === "template" ? "template"
        : intent === "ai" ? "ai" : null;
      nav(source ? `/brief/new?source=${source}` : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-md mx-auto px-6 py-12 sm:py-16">
        <div className="pf-label mb-4">Create your business account</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-3">Get started.</h1>
        <p className="text-sm text-[var(--pf-text-2)] mb-8">
          Free to sign up. Order AI-designed prints in under 60 seconds.
        </p>
        <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
          <div>
            <label className="pf-label block mb-2">Full name</label>
            <input data-testid="signup-name" required className="pf-input" value={f.name} onChange={update("name")} />
          </div>
          <div>
            <label className="pf-label block mb-2">Email</label>
            <input data-testid="signup-email" type="email" required className="pf-input" value={f.email} onChange={update("email")} />
          </div>
          <div>
            <label className="pf-label block mb-2">Password (min 6)</label>
            <input data-testid="signup-password" type="password" required minLength={6} className="pf-input" value={f.password} onChange={update("password")} />
          </div>
          <div>
            <label className="pf-label block mb-2">Company (optional)</label>
            <input data-testid="signup-company" className="pf-input" value={f.company} onChange={update("company")} />
          </div>
          <button type="submit" disabled={loading} data-testid="signup-submit" className="pf-btn-primary w-full justify-center mt-4">
            {loading ? "Creating…" : "Create account →"}
          </button>
        </form>
        <div className="mt-8 text-sm text-[var(--pf-text-2)]">
          Already have one? <Link to="/login" data-testid="signup-login-link" className="text-[var(--pf-text)] underline underline-offset-4 font-medium">Sign in</Link>
        </div>
        <div className="mt-12 p-4 border border-[var(--pf-border)] bg-white/60 rounded-lg text-xs text-[var(--pf-text-3)] leading-relaxed" data-testid="signup-invite-notice">
          Are you a <strong>designer, print vendor, or agency admin?</strong>{" "}
          Access is invite-only — ask your admin to send you an invitation email.
        </div>
      </div>
    </div>
  );
}
